/**
 * Leviathan Web - Customer Locations (Ship-To) List
 * Quasar Prime LLC
 */

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { MapPin, Plus, Building2, Truck, Package, ArrowLeft } from 'lucide-react';

import { API_URL } from '@/config/api';

interface CustomerLocation {
  id: string;
  name: string;
  locationCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  locationType: string | null;
  isActive: boolean;
  _count: {
    tanks: number;
    orders: number;
  };
}

interface Customer {
  id: string;
  name: string;
  customerNumber: string;
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  PROJECT_SITE: 'Project Site',
  WAREHOUSE: 'Warehouse',
  FLEET_YARD: 'Fleet Yard',
  OFFICE: 'Office',
  OTHER: 'Other',
};

export default function CustomerLocations() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [locations, setLocations] = useState<CustomerLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCustomer();
    fetchLocations();
  }, [customerId, showInactive]);

  const fetchCustomer = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomer(data.customer);
      }
    } catch (err) {
      console.error('Failed to fetch customer:', err);
    }
  };

  const fetchLocations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (!showInactive) params.append('isActive', 'true');

      const response = await fetch(`${API_URL}/api/customer-locations?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setLocations(data.locations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch locations');
    } finally {
      setIsLoading(false);
    }
  };

  const getLocationTypeLabel = (type: string | null) => {
    return type ? LOCATION_TYPE_LABELS[type] || type : 'Not specified';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
          <div className="pl-6">
            <Link
              to={`/customers/${customerId}`}
              className="back-link mb-3"
            >
              <ArrowLeft size={14} />
              Back to {customer?.name || 'Customer'}
            </Link>
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              Ship-To Locations
            </h1>
            <p className="text-text-secondary text-sm font-mono mt-1">
              {locations.length} location{locations.length !== 1 ? 's' : ''} • Delivery addresses
              and project sites
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={`/customers/${customerId}/locations/wizard`}
            className="flex items-center gap-2 bg-amber hover:bg-amber/90 text-background font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm"
          >
            <Plus size={16} />
            New Location
          </Link>
          <Link
            to={`/customers/${customerId}/locations/new`}
            className="flex items-center gap-2 bg-panel-dark border border-panel-border hover:border-amber/50 text-text-secondary font-mono py-2 px-4 transition-all text-sm"
          >
            Quick Add
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="form-checkbox bg-background border-panel-border text-amber focus:ring-amber"
          />
          <span className="text-text-secondary font-mono text-sm">Show inactive locations</span>
        </label>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      {/* Locations Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-text-tertiary font-mono">Loading...</div>
      ) : locations.length === 0 ? (
        <div className="text-center py-12">
          <MapPin size={48} className="mx-auto mb-4 text-text-tertiary" />
          <div className="text-text-tertiary font-mono mb-4">No locations found</div>
          <Link
            to={`/customers/${customerId}/locations/new`}
            className="text-amber hover:text-amber/80 font-mono"
          >
            Add your first location →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <div
              key={location.id}
              onClick={() => navigate(`/customers/${customerId}/locations/${location.id}`)}
              className="relative group cursor-pointer"
            >
              {/* Corner decorations */}
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border hover:border-amber/50 p-6 transition-all h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-text-primary font-mono font-semibold text-lg">
                        {location.name}
                      </h3>
                      {!location.isActive && (
                        <span className="px-2 py-0.5 text-xs font-mono bg-gray-500/20 text-gray-400 border border-gray-500">
                          INACTIVE
                        </span>
                      )}
                    </div>
                    {location.locationCode && (
                      <div className="text-text-tertiary font-mono text-xs">
                        Code: {location.locationCode}
                      </div>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-1 mb-4 text-sm font-mono text-text-secondary">
                  {location.address && <div>{location.address}</div>}
                  {(location.city || location.state) && (
                    <div>
                      {location.city}
                      {location.city && location.state ? ', ' : ''}
                      {location.state} {location.zip}
                    </div>
                  )}
                  {!location.address && !location.city && (
                    <div className="text-text-tertiary italic">No address</div>
                  )}
                </div>

                {/* Type */}
                <div className="mb-4">
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-background/50 text-text-tertiary border border-panel-border">
                    <Building2 size={12} />
                    {getLocationTypeLabel(location.locationType)}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-panel-border">
                  <div className="flex items-center gap-2">
                    <Truck size={14} className="text-amber" />
                    <div>
                      <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
                        Tanks
                      </div>
                      <div className="text-text-primary font-display font-bold text-xl">
                        {location._count.tanks}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-steel" />
                    <div>
                      <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
                        Orders
                      </div>
                      <div className="text-text-primary font-display font-bold text-xl">
                        {location._count.orders}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
